// client/src/components/pages/OperationOverview/routeSort.ts (ST-00217)
//
// The Route Detail table's column sort, moved out of index.tsx when the
// original Route Detail tab was retired. ST-00218 P3: figures are the server's adjusted
// ones when sent (overviewKpis); an n/a variance sorts last. The order is the original table's:
// every figure is BF/CF-adjusted like the cards, names compare without case,
// the first click on a column sorts ascending and a second click on the same
// column descending. The PDF export uses the same order.
import { adjustedDispatch, adjustedDispatchCount, isVarianceNa } from './overviewKpis';
import type { OverviewGrouping } from '../../../types/clientPages/operationOverview';
import { hasVarianceBase, varianceOf } from '../../../utils/variance';

export type RouteSortKey =
  | 'name'
  | 'dispatched'
  | 'received'
  | 'loadsDispatched'
  | 'loadsReceived'
  | 'difference'
  | 'variance';

export interface RouteSortConfig {
  /** null: unsorted, the server's order. */
  key: RouteSortKey | null;
  direction: 'asc' | 'desc';
}

export const UNSORTED: RouteSortConfig = { key: null, direction: 'asc' };

/** The sort after a click on `key`'s column heading. */
export const nextSort = (current: RouteSortConfig, key: RouteSortKey): RouteSortConfig => ({
  key,
  direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
});

/**
 * (received - adjusted dispatch) / adjusted dispatch, in %; 0 when nothing was dispatched.
 * ST-00218 P3: the server's `variancePct` when it sent one; null = n/a (no / partial receipt
 * data on a "Truck + time" route).
 * ST-00298: only over an adjusted dispatch above 0.5 t (utils/variance) — below it 0 when
 * nothing was received either, else n/a, whatever the server sent (an older answer divided by
 * the float dust of a fully carried-forward route).
 */
export const routeVariance = (g: OverviewGrouping): number | null => {
  if (isVarianceNa(g)) return null;
  const adj = adjustedDispatch(g);
  if (hasVarianceBase(adj) && typeof g.variancePct === 'number' && Number.isFinite(g.variancePct))
    return g.variancePct;
  return varianceOf(g.totalReceiptWeight || 0, adj);
};

const sortValue = (g: OverviewGrouping, key: RouteSortKey): string | number | null => {
  switch (key) {
    case 'name':
      return (g.name || '').toLowerCase();
    case 'dispatched':
      return adjustedDispatch(g);
    case 'received':
      return g.totalReceiptWeight || 0;
    case 'loadsDispatched':
      return adjustedDispatchCount(g);
    case 'loadsReceived':
      return g.totalReceiptCount || 0;
    case 'difference':
      return (g.totalReceiptWeight || 0) - adjustedDispatch(g);
    case 'variance':
      return routeVariance(g);
  }
};

/** A sorted copy of `groupings` (the input is left as it is). */
export const sortGroupings = <G extends OverviewGrouping>(
  groupings: G[],
  sort: RouteSortConfig
): G[] => {
  const { key, direction } = sort;
  if (!key) return groupings;
  const sign = direction === 'asc' ? 1 : -1;
  return [...groupings].sort((a, b) => {
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    // n/a (a variance the server could not give) goes last whichever way the column sorts.
    if (av === null || bv === null) return av === bv ? 0 : av === null ? 1 : -1;
    if (av < bv) return -sign;
    if (av > bv) return sign;
    return 0;
  });
};
